/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import BatchSearch from "./components/BatchSearch";
import BatchList from "./components/BatchList";
import moment from "moment";
import { AiOutlineUpload } from "react-icons/ai";
import { Button } from "@chakra-ui/react";
import BatchUploadModal from "./components/BatchUploadModal";
import BatchToolbar from "./components/BatchToolbar";
import { usePageWebSocket } from "../../../hooks/usePageWebSocket";

interface Filters {
  [key: string]: any;
}
interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const initialFilters: Filters = {
  date_type: "c",
  from: initFrom,
  to: initTo,
  asset: "",
  status: "",
  provider_account: "",
  payout_method: "",
};

const Batch = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    initialDateRangeFilter
  );
  const [isOpenbatchUploadModal, setIsOpenbatchUploadModal] =
    useState<boolean>(false);
  const [providerList, setProviderList] = useState<any>(null);
  const [assetList, setAssetList] = useState<any>(null);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // Async batch processing state
  const [processingBatchIds, setProcessingBatchIds] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("processingBatch_")) {
        ids.add(key.replace("processingBatch_", ""));
      }
    }
    return ids;
  });

  const { received } = usePageWebSocket("batch_list");

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  // Listen for WebSocket messages
  useEffect(() => {
    if (!received) return;
    if (received.messageType !== "batch") return;

    const batchId = received.data?.batch_id;
    const isProcessingStarted = !!received.data?.processing;

    if (isProcessingStarted) {
      // Processing started — track + update row status directly
      if (batchId) {
        setProcessingBatchIds(prev => {
          const next = new Set(prev);
          next.add(batchId);
          return next;
        });
        localStorage.setItem(`processingBatch_${batchId}`, "true");
        // Update status in table data directly if batch_status is provided
        const newStatus = received.data?.batch_status;
        if (newStatus) {
          setTableData((prev: any) => prev.map((row: any) =>
            row.id === batchId ? { ...row, status: newStatus } : row
          ));
        }
      }
    } else {
      // SNS — clear tracking + refetch
      if (batchId) {
        setProcessingBatchIds(prev => {
          const next = new Set(prev);
          next.delete(batchId);
          return next;
        });
        localStorage.removeItem(`processingBatch_${batchId}`);
      }
      fetchBatchSilent({ page, limit: pageSize, lastkey: page > 0 ? (lastKeys[page - 1] || "") : "", ...filters });
    }
  }, [received]); // eslint-disable-line

  const fetchBatchSilent = async (payload: PayloadType = {}) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey
      };

      if (payload?.date_type === "c") {
        queryObject.from = moment(selectedDates[0], "DD/MM/YYYY").format("YYYY-MM-DD 00:00");
        queryObject.to = moment(selectedDates[1], "DD/MM/YYYY").format("YYYY-MM-DD 23:59");
      } else {
        queryObject.pfrom = moment(selectedDates[0], "DD/MM/YYYY").format("YYYY-MM-DD 00:00");
        queryObject.pto = moment(selectedDates[1], "DD/MM/YYYY").format("YYYY-MM-DD 23:59");
      }

      if (payload?.status) queryObject.status = payload.status;
      if (payload?.asset) queryObject.asset = payload.asset;
      if (payload?.provider_account) queryObject.provider_account = payload.provider_account;
      if (payload?.payout_method) queryObject.payout_method = payload.payout_method;

      if (userRole === 'superadmin' || userRole === 'merchant') {
        const subaccountId = localStorage.getItem("subaccountIdToFilterBatch");
        if (subaccountId && subaccountId !== "") queryObject.subaccount = subaccountId;
      }

      const queryString = Object.keys(queryObject)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`)
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/batch/func/get-all-batch?${queryString}`
      );

      let newData = response.data.batches?.payload;
      if (newData && newData.length !== 0) {
        setTableData(newData);
      }
    } catch (error: any) {
      console.error("Error silently fetching batch data", error);
    }
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("subaccountIdToFilterBatch");

      setIsLoading(true);
      await fetchBatch({ page: 0, limit: rowPerPage, lastkey: "", ...filters }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      await fetchProviders(signal);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      if (userRole === "superadmin") {
        await fetchAssetsForSuperadmin(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else if (userRole === "merchant") {
        await fetchAssetsForMerchant(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else {
        await fetchAssets(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      }
      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []);


  const fetchProviders = async (signal?: AbortSignal) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-method?type=batch&entityid=all`,
        { signal }
      );

      setProviderList(response.data.provider);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      setIsLoading(false);
      console.error(error);
      setProviderList([]);
    }
  };

  const fetchAssetsForSuperadmin = async (signal?: AbortSignal) => {
    try {
      let requestUrl = `/api/datasource/assets`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl,
        { signal }
      );

      const data = response?.data;

      setAssetList(data);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching assets", error);
    }
  };

  const fetchAssetsForMerchant = async (signal?: AbortSignal) => {
    const merchantId = localStorage.getItem("merchantId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/merchant/get-merchant-by-id?merchantid=${merchantId}`,
        { signal }
      );

      const accountData = response.data.merchant?.payload;

      if (!accountData) {
        throw new Error('No merchant data found');
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            crypto: method.crypto
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching merchant details:", error);
    }
  };

  const fetchAssets = async (signal?: AbortSignal) => {
    const subaccountId = localStorage.getItem("subaccountId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`,
        { signal }
      );

      const accountData = response.data.subaccount?.payload;

      if (!accountData) {
        throw new Error('No subaccount data found');
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            decimal: method.decimal ?? 2,
            crypto: method.crypto
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching subaccount details:", error);
    }
  };

  const fetchBatch = async (payload: PayloadType = {}, isReset = false, signal?: AbortSignal, requestId?: number) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey
      };

      if (payload?.date_type === "c") {
        queryObject.from = moment(
          !isReset ? selectedDates[0] : initFrom,
          "DD/MM/YYYY"
        ).format("YYYY-MM-DD 00:00");
        queryObject.to = moment(
          !isReset ? selectedDates[1] : initTo,
          "DD/MM/YYYY"
        ).format("YYYY-MM-DD 23:59");
      } else {
        queryObject.pfrom = moment(
          !isReset ? selectedDates[0] : initFrom,
          "DD/MM/YYYY"
        ).format("YYYY-MM-DD 00:00");
        queryObject.pto = moment(
          !isReset ? selectedDates[1] : initTo,
          "DD/MM/YYYY"
        ).format("YYYY-MM-DD 23:59");
      }

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.asset !== "" && payload?.asset !== undefined) {
        queryObject.asset = payload?.asset;
      }

      if (
        payload?.provider_account !== "" &&
        payload?.provider_account !== undefined
      ) {
        queryObject.provider_account = payload?.provider_account;
      }

      if (
        payload?.payout_method !== "" &&
        payload?.payout_method !== undefined
      ) {
        queryObject.payout_method = payload?.payout_method;
      }

      if (userRole === 'superadmin' || userRole === 'merchant') {
        const subaccountId = localStorage.getItem("subaccountIdToFilterBatch");

        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          queryObject.subaccount = subaccountId;
        }
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-all-batch?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.batches?.payload;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record
      let newLastKey = response.data.batches?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchBatch(newFilters);
    setIsLoading(false);
  };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchBatch({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const handleSearchClear = () => {
    localStorage.removeItem("subaccountIdToFilterBatch");

    handleResetTable();
  };

  const handleResetTable = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setPage(0);
    setCurrentLastKey("");
    setLastKeys([]);
    setSelectedDates(initialDateRangeFilter);
    await fetchBatch({ page: 0, limit: rowPerPage, lastkey: "", ...initialFilters }, true);
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const showBatchUploadModal = () => {
    setIsOpenbatchUploadModal(true);
  };

  const closeBatchUploadModal = () => {
    setIsOpenbatchUploadModal(false);
  };

  const triggerReloadBatchList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchBatch({ page: 0, limit: rowPerPage, lastkey: "", ...initialFilters }, true);
    setIsLoading(false);
    setIsFilterLoading(false);
  }

  return (
    <>
      {/* { (userRole === 'admin' || userRole === 'subaccount') &&
        <>
          <BatchUploadModal triggerReloadBatchList={triggerReloadBatchList} isOpen={isOpenbatchUploadModal} onClose={() => closeBatchUploadModal()} />

          <div className="flex justify-start mt-6 mb-3">
            <Button
              isDisabled={isLoading}
              onClick={() => showBatchUploadModal()}
              className="rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:text-white dark:hover:bg-primary-300 dark:active:bg-primary-200"
            >
              <AiOutlineUpload className="h-4 w-4" />&nbsp;UPLOAD
            </Button>
          </div>
        </>
      } */}

      <BatchToolbar
        isLoading={isLoading || isFilterLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadBatchList}
      />

      <BatchSearch
        showFilters={showFilters}
        isLoading={isLoading}
        setIsFilterLoading={setIsFilterLoading}
        isFilterLoading={isFilterLoading}
        filters={filters}
        setFilters={setFilters}
        setSelectedDates={setSelectedDates}
        selectedDates={selectedDates}
        providerList={providerList}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
        assetList={assetList}
      />
      <BatchList
        userPermissions={userPermissions}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
        triggerReloadBatchList={triggerReloadBatchList}
        processingBatchIds={processingBatchIds}
      />
    </>
  );
};

export default Batch;
